import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import SEO from '../components/SEO';
import OptimizedImage from '../components/OptimizedImage';
import { trackEvent } from '../lib/ga';

const EnterDrawing = () => {
  const [entryType, setEntryType] = useState('customer');
  const [drawingForm, setDrawingForm] = useState({
    name: '', email: '', phone: '', smsConsent: false, comment: '',
    companyName: '', companyPhone: '', websiteUrl: '', position: '', bestContactMethod: 'email'
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [formLoadTime] = useState(Date.now());

  const containsHtml = (str) => str ? /<[^>]*>/g.test(str) : false;
  const isFakePhone = (phone) => {
    if (!phone) return true;
    const digits = phone.replace(/\D/g, '');
    return digits.length < 10 || digits.includes('555') || new Set(digits).size <= 2;
  };
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateForm = () => {
    if (honeypot) return { valid: false, error: 'Spam detected' };
    if (Date.now() - formLoadTime < 3000) return { valid: false, error: 'Please take your time filling out the form' };
    if (!drawingForm.name.trim()) return { valid: false, error: 'Name is required' };
    if (!isValidEmail(drawingForm.email)) return { valid: false, error: 'Valid email is required' };
    if (isFakePhone(drawingForm.phone)) return { valid: false, error: 'Valid phone number is required (no 555 numbers)' };
    if (!drawingForm.smsConsent) return { valid: false, error: 'SMS consent is required to enter the drawing' };
    const fields = [drawingForm.name, drawingForm.email, drawingForm.phone, drawingForm.comment];
    for (const field of fields) { if (containsHtml(field)) return { valid: false, error: 'HTML is not allowed' }; }
    if (entryType === 'vendor') {
      if (!drawingForm.companyName.trim()) return { valid: false, error: 'Company name is required' };
      if (isFakePhone(drawingForm.companyPhone)) return { valid: false, error: 'Valid company phone is required' };
      if (!drawingForm.websiteUrl.trim()) return { valid: false, error: 'Website URL is required' };
      if (!drawingForm.position.trim()) return { valid: false, error: 'Position is required' };
    }
    return { valid: true };
  };

  const handleDrawingSubmit = async (e) => {
    e.preventDefault();
    const validation = validateForm();
    if (!validation.valid) { toast({ title: 'Error', description: validation.error, variant: 'destructive' }); return; }
    setFormSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/drawings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: drawingForm.name, email: drawingForm.email, phone: drawingForm.phone,
          sms_consent: drawingForm.smsConsent, comment: drawingForm.comment || null,
          entry_type: entryType,
          company_name: entryType === 'vendor' ? drawingForm.companyName : null,
          company_phone: entryType === 'vendor' ? drawingForm.companyPhone : null,
          website_url: entryType === 'vendor' ? drawingForm.websiteUrl : null,
          position: entryType === 'vendor' ? drawingForm.position : null,
          best_contact_method: drawingForm.bestContactMethod
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to submit entry');
      setSubmitted(true);
      trackEvent('drawing_entry_submitted', { entry_type: entryType, source: 'enter_page' });
      toast({ title: "You're Entered!", description: 'Good luck in the weekly drawing!' });
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setFormSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed',
          fontFamily: 'Outfit, sans-serif'
        }}
      >
        <div className="bg-white p-10 md:p-14 rounded-3xl shadow-2xl max-w-lg w-full text-center">
          <OptimizedImage
            src="/images/logo/logo-circle.png"
            alt="1st Ave ICP"
            className="w-24 h-24 mx-auto mb-6 rounded-full shadow-lg"
            width={96} height={96}
          />
          <div className="text-6xl mb-4">🎉</div>
          <h1
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ fontFamily: 'Rowdies, cursive' }}
          >
            You're Entered!
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            Good luck in the weekly drawing! Winners are announced each week via text and email.
          </p>
          <p className="text-sm text-gray-400">You can close this page now.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{
        backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed',
        fontFamily: 'Outfit, sans-serif'
      }}
    >
      <SEO
        title="Enter Our Weekly Ice Cream Giveaway Drawing"
        description="Enter the weekly ice cream giveaway drawing at 1st Ave ICP in Mineral Wells, TX. Absolutely free to enter! Win free ice cream and exclusive offers."
        keywords="free ice cream drawing, weekly giveaway Mineral Wells TX, 1st Ave ICP contest, free dessert"
        canonical="/enter"
      />

      <div className="bg-white p-8 md:p-10 rounded-3xl shadow-2xl max-w-lg w-full">
        {/* Logo */}
        <div className="flex justify-center -mt-16 mb-4">
          <OptimizedImage
            src="/images/logo/logo-circle.png"
            alt="1st Ave ICP"
            className="w-24 h-24 rounded-full shadow-xl bg-white border-4 border-white"
            width={96} height={96} priority={true}
          />
        </div>

        <div className="text-center mb-6">
          <h1
            className="text-2xl md:text-3xl font-bold text-black mb-1"
            style={{ fontFamily: 'Rowdies, cursive' }}
            data-testid="enter-drawing-title"
          >
            Enter Our Weekly Drawing!
          </h1>
          <p className="text-gray-600 text-sm">Win free ice cream & exclusive offers!</p>
        </div>

        <form onSubmit={handleDrawingSubmit} className="space-y-4" data-testid="enter-drawing-form">
          {/* Honeypot */}
          <input type="text" name="website_confirm" value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
            style={{ position: 'absolute', left: '-9999px', opacity: 0 }} tabIndex="-1" autoComplete="off" />

          {/* Customer/Vendor */}
          <div className="bg-pink-50 p-3 rounded-lg">
            <label className="block text-sm font-bold text-gray-700 mb-2">I am a:</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="entryTypeLanding" value="customer" checked={entryType === 'customer'}
                  onChange={(e) => setEntryType(e.target.value)} className="w-4 h-4 accent-pink-500" />
                <span className="font-medium">Customer</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="entryTypeLanding" value="vendor" checked={entryType === 'vendor'}
                  onChange={(e) => setEntryType(e.target.value)} className="w-4 h-4 accent-pink-500" />
                <span className="font-medium">Vendor</span>
              </label>
            </div>
          </div>

          {/* Basic Fields */}
          <div className="grid grid-cols-2 gap-3">
            <input type="text" placeholder="Name *" value={drawingForm.name} data-testid="enter-name-input"
              onChange={(e) => setDrawingForm({...drawingForm, name: e.target.value})}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none" required />
            <input type="email" placeholder="Email *" value={drawingForm.email} data-testid="enter-email-input"
              onChange={(e) => setDrawingForm({...drawingForm, email: e.target.value})}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none" required />
          </div>

          <input type="tel" placeholder="Phone Number * (for text notifications)" value={drawingForm.phone} data-testid="enter-phone-input"
            onChange={(e) => setDrawingForm({...drawingForm, phone: e.target.value})}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none" required />

          {/* Vendor Fields */}
          {entryType === 'vendor' && (
            <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-bold text-gray-700">Vendor Information</p>
              <input type="text" placeholder="Company Name *" value={drawingForm.companyName}
                onChange={(e) => setDrawingForm({...drawingForm, companyName: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none" />
              <input type="tel" placeholder="Company Phone *" value={drawingForm.companyPhone}
                onChange={(e) => setDrawingForm({...drawingForm, companyPhone: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none" />
              <input type="text" placeholder="Website URL *" value={drawingForm.websiteUrl}
                onChange={(e) => setDrawingForm({...drawingForm, websiteUrl: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none" />
              <input type="text" placeholder="Your Position *" value={drawingForm.position}
                onChange={(e) => setDrawingForm({...drawingForm, position: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none" />
              <select value={drawingForm.bestContactMethod}
                onChange={(e) => setDrawingForm({...drawingForm, bestContactMethod: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none">
                <option value="email">Best Contact: Email</option>
                <option value="phone">Best Contact: Phone</option>
                <option value="text">Best Contact: Text</option>
              </select>
            </div>
          )}

          <textarea placeholder="Comment (optional)" rows="3" value={drawingForm.comment}
            onChange={(e) => setDrawingForm({...drawingForm, comment: e.target.value})}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none resize-none" />

          {/* SMS Consent */}
          <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={drawingForm.smsConsent} data-testid="enter-sms-consent"
                onChange={(e) => setDrawingForm({...drawingForm, smsConsent: e.target.checked})}
                className="w-5 h-5 mt-0.5 accent-teal-500" required />
              <span className="text-sm text-gray-700">
                <strong className="text-teal-700">Required:</strong> I agree to receive text messages from 1st Ave ICP about the weekly drawing, promotions, and special offers. Message frequency varies. Message & data rates may apply. Reply STOP to unsubscribe.
              </span>
            </label>
          </div>

          <Button type="submit" disabled={formSubmitting || !drawingForm.smsConsent} data-testid="enter-submit-btn"
            className="bg-gradient-to-r from-[#E91E8C] to-[#C4197A] hover:from-[#C4197A] hover:to-[#A01565] text-white font-bold px-12 py-4 rounded-full shadow-lg w-full disabled:opacity-50">
            {formSubmitting ? 'Entering...' : 'Enter Drawing'}
          </Button>

          <p className="text-xs text-gray-500 text-center">
            One entry per person. Winners notified via email & text.
          </p>
        </form>
      </div>
    </div>
  );
};

export default EnterDrawing;
